// =============================================================================
// src/components/UpdateAvailable.jsx - "PracticeOS was updated" bar.
// Checks every 5 minutes and when the tab comes back into view whether a
// newer build is deployed (lib/versionCheckCore). If so, a small bar offers
// Reload. It never reloads by itself: a note or form may be half typed.
// =============================================================================
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { C } from "../lib/tokens";
import { entryFromHtml, entryFromScripts, isNewerBuild } from "../lib/versionCheckCore";

const EVERY_MS = 5 * 60 * 1000;

function runningEntry() {
  if (typeof document === "undefined") return null;
  return entryFromScripts(Array.from(document.querySelectorAll("script[src]")).map((s) => s.getAttribute("src")));
}

export default function UpdateAvailable() {
  const [newer, setNewer] = useState(false);

  useEffect(() => {
    const running = runningEntry();
    if (!running) return undefined; // dev server: nothing to compare
    let stopped = false;
    const check = async () => {
      if (stopped || document.visibilityState === "hidden") return;
      try {
        const res = await fetch("/index.html?v=" + Date.now(), { cache: "no-store", credentials: "same-origin" });
        if (!res.ok) return;
        if (isNewerBuild(running, entryFromHtml(await res.text()))) setNewer(true);
      } catch (e) { /* offline: try again later */ }
    };
    const timer = setInterval(check, EVERY_MS);
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { stopped = true; clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); };
  }, []);

  if (!newer || typeof document === "undefined") return null;
  // Portalled like the toasts: a transformed ancestor would clip it.
  return createPortal(
    <div data-testid="update-available" role="status" className="pos-no-print" style={{
      position: "fixed", left: "50%", top: 12, transform: "translateX(-50%)", zIndex: 4100,
      display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "center",
      maxWidth: "calc(100vw - 32px)", boxSizing: "border-box",
      padding: "8px 10px 8px 14px", borderRadius: 10,
      background: C.bgPrimary, color: C.textPrimary, border: "0.5px solid " + C.borderMid,
      boxShadow: "0 6px 24px rgba(0,0,0,0.14)", fontSize: 13,
    }}>
      <span>PracticeOS was updated. Reload to use the latest version.</span>
      <button type="button" onClick={() => window.location.reload()} style={{
        border: "none", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontWeight: 600, fontSize: 13,
        background: "var(--brand-primary)", color: "var(--brand-contrast, #fff)", fontFamily: "inherit",
      }}>Reload</button>
      <button type="button" aria-label="Later" onClick={() => setNewer(false)} style={{
        border: "none", background: "transparent", color: C.textSecondary, cursor: "pointer", fontSize: 12, fontFamily: "inherit",
      }}>Later</button>
    </div>,
    document.body,
  );
}
